import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Mail, Inbox, RefreshCcw, Forward, Reply } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}

export default function RecentEmailsWidget() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchEmails = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/emails/recent?limit=6`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setEmails(data.emails || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails();
  }, []);

  const handleOpen = async (email) => {
    setDetailLoading(true);
    setSelected(email); // optimistic — show previewed fields instantly
    try {
      const res = await fetch(`${API}/emails/${email.email_id}`, { credentials: "include" });
      if (res.ok) {
        const full = await res.json();
        setSelected(full);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to load email");
      }
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <>
      <Card data-testid="recent-emails-widget">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg font-bold tracking-tight flex items-center gap-2">
              <Inbox className="w-4 h-4" />
              Recent Emails
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={fetchEmails} className="h-7 w-7" data-testid="refresh-emails-btn">
              <RefreshCcw className="w-3.5 h-3.5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2 animate-pulse">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 bg-slate-100 rounded" />
              ))}
            </div>
          ) : emails.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">
              <Mail className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              No emails yet. Send one from any lead to get started.
            </div>
          ) : (
            <div className="space-y-1">
              {emails.map((e) => (
                <button
                  key={e.email_id}
                  onClick={() => handleOpen(e)}
                  className="w-full text-left p-2 rounded-md hover:bg-slate-50 transition-colors"
                  data-testid={`email-row-${e.email_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {e.lead_name || e.to_email}
                        </p>
                        <Badge variant="outline" className="text-[10px] uppercase flex-shrink-0">
                          {e.status || "sent"}
                        </Badge>
                      </div>
                      <p className="text-xs font-medium text-slate-700 truncate">{e.subject}</p>
                      <p className="text-xs text-slate-500 truncate">{e.preview}</p>
                    </div>
                    <span className="text-[10px] text-slate-400 flex-shrink-0 whitespace-nowrap">
                      {formatDate(e.sent_at)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Full email viewer */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="email-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg">{selected?.subject || "Email"}</DialogTitle>
            <DialogDescription>
              <span className="text-xs text-slate-500">
                From <span className="font-mono">{selected?.user_name || "—"}</span>
                {" · "}To <span className="font-mono">{selected?.to_email}</span>
                {" · "}
                {selected?.sent_at ? new Date(selected.sent_at).toLocaleString() : ""}
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {selected?.lead_name && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span>Lead:</span>
                <Badge variant="secondary" className="text-xs">
                  {selected.lead_name}
                </Badge>
                {selected.lead_id && (
                  <a
                    href={`/leads/${selected.lead_id}`}
                    className="text-blue-600 hover:underline ml-auto"
                  >
                    Open lead →
                  </a>
                )}
              </div>
            )}

            {detailLoading ? (
              <div className="h-24 bg-slate-50 animate-pulse rounded" />
            ) : (
              <div
                className="prose prose-sm max-w-none p-3 rounded-md bg-slate-50 border border-slate-200 text-slate-700 whitespace-pre-wrap break-words"
                data-testid="email-body"
              >
                {selected?.body || <span className="text-slate-400 italic">No body</span>}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-2 flex-row">
            {selected?.lead_id && (
              <a href={`/leads/${selected.lead_id}`} className="flex-1 sm:flex-none">
                <Button variant="outline" className="w-full" data-testid="email-reply-btn">
                  <Reply className="w-3.5 h-3.5 mr-2" />
                  Reply from Lead
                </Button>
              </a>
            )}
            <Button
              variant="outline"
              onClick={() => {
                const body = `\n\n--- Forwarded ---\nFrom: ${selected?.user_name || ""}\nTo: ${selected?.to_email}\nSubject: ${selected?.subject}\n\n${selected?.body || ""}`;
                navigator.clipboard.writeText(body);
                toast.success("Email copied — paste into a new compose");
              }}
              data-testid="email-forward-btn"
            >
              <Forward className="w-3.5 h-3.5 mr-2" />
              Copy to Forward
            </Button>
            <Button onClick={() => setSelected(null)} data-testid="email-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
